// Blåtur page - all trips with group photo and participants, a participation overview and the rules
// Trips are read from the 'events' collection (event_type 'Blåtur🌍'), which requires login

import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiDownload, FiUsers, FiMapPin } from 'react-icons/fi';
import {
  useAuthUser, useBlaturTrips, buildParticipationMatrix, exportBlaturToExcel,
  tripYear, tripTitle, tripCover,
} from './blaturData';
import BlaturRegler from './BlaturRegler';
import BlaturMedlemmer from './BlaturMedlemmer';
import { useRoles } from './adminData';
import { SmartImage } from './imageUtils';

const TABS = [
  { key: 'turer', label: 'Turer' },
  { key: 'oversikt', label: 'Deltakelse' },
  { key: 'medlemmer', label: 'Invitasjonsliste' },
  { key: 'regler', label: 'Regler og info' },
];

function TripCard({ trip }) {
  const cover = tripCover(trip);
  const participants = trip.Deltagelse || [];
  return (
    <Link to={`/blatur/${trip.id}`} className="blatur-card">
      <div className="blatur-card-image">
        {cover ? <SmartImage src={cover} size={800} alt={`Gruppebilde ${tripTitle(trip)}`} /> : <div className="blatur-card-placeholder"><FiMapPin /></div>}
        <span className="blatur-card-year">{tripYear(trip)}</span>
      </div>
      <div className="blatur-card-body">
        <h2 className="blatur-card-title">{tripTitle(trip)}</h2>
        <p className="blatur-card-hosts"><span>Arrangører</span> {(trip.Vert || []).join(', ') || '–'}</p>
        <p className="blatur-card-participants">
          <FiUsers /> {participants.length} deltakere
        </p>
        <p className="blatur-card-names">{participants.join(', ')}</p>
      </div>
    </Link>
  );
}

function ParticipationTable({ trips, userMap }) {
  const { columns, rows } = useMemo(() => buildParticipationMatrix(trips), [trips]);
  return (
    <>
      <div className="blatur-matrix-wrap">
        <table className="blatur-matrix">
          <thead>
            <tr>
              <th className="blatur-matrix-name">Navn</th>
              {columns.map(trip => (
                <th key={trip.id} title={tripTitle(trip)}>
                  <Link to={`/blatur/${trip.id}`}>{tripYear(trip)}</Link>
                </th>
              ))}
              <th>Turer</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const userId = userMap[row.name.toLowerCase()];
              return (
                <tr key={row.name}>
                  <td className="blatur-matrix-name">
                    {userId ? <Link to={`/profil/${userId}`} className="user-link">{row.name}</Link> : row.name}
                  </td>
                  {row.cells.map((cell, i) => (
                    <td key={columns[i].id} className={cell ? `cell-${cell}` : ''}>
                      {cell === 'host' ? '👑' : cell === 'participant' ? '✓' : ''}
                    </td>
                  ))}
                  <td className="blatur-matrix-total">{row.total}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="blatur-legend">👑 arrangør · ✓ deltok</p>
    </>
  );
}

function Blatur() {
  const user = useAuthUser();
  const { trips, userMap, userNames, profileImages, loading } = useBlaturTrips(user);
  const [tab, setTab] = useState('turer');
  const { config } = useRoles(user);

  // Upcoming trip from config/blatur, e.g. "Blåtur 2027: Njaal og Anders S"
  const nextTripYear = config?.year;
  const latestTripYear = Math.max(0, ...trips.map(tripYear));
  const nextOrganizers = (config?.organizers || []).map(uid => userNames[uid]).filter(Boolean);

  const needsTrips = tab === 'turer' || tab === 'oversikt';

  return (
    <div className="blatur-page">
      <div className="blatur-header">
        <h1>🌍 Blåtur</h1>
        <p className="blatur-intro">Årlig tur med ukjent destinasjon, siden Pula 2017.</p>
        {nextTripYear > latestTripYear && nextOrganizers.length > 0 && (
          <p className="blatur-next">Blåtur {nextTripYear}: {nextOrganizers.join(' og ')} arrangerer 👑</p>
        )}
      </div>

      <div className="blatur-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`filter-chip${tab === t.key ? ' active' : ''}`}
            onClick={() => setTab(t.key)}
          >{t.label}</button>
        ))}
        {needsTrips && user && trips.length > 0 && (
          <button className="export-icon-button blatur-export" onClick={() => exportBlaturToExcel(trips)} title="Last ned deltakerlister (Excel)">
            <FiDownload />
          </button>
        )}
      </div>

      {needsTrips && user === null && (
        <div className="blatur-empty">Logg inn for å se turene og deltakerlistene.</div>
      )}
      {needsTrips && user && loading && <div className="blatur-empty">Laster turer…</div>}
      {needsTrips && user && !loading && trips.length === 0 && (
        <div className="blatur-empty">Ingen blåturer registrert ennå.</div>
      )}

      {tab === 'turer' && user && trips.length > 0 && (
        <div className="blatur-grid">
          {trips.map(trip => <TripCard key={trip.id} trip={trip} />)}
        </div>
      )}

      {tab === 'oversikt' && user && trips.length > 0 && (
        <ParticipationTable trips={trips} userMap={userMap} />
      )}

      {tab === 'medlemmer' && user && <BlaturMedlemmer user={user} myName={userNames[user.uid] || ''} trips={trips} profileImages={profileImages} userMap={userMap} />}
      {tab === 'medlemmer' && user === null && <div className="blatur-empty">Logg inn for å se invitasjonslisten.</div>}

      {tab === 'regler' && <BlaturRegler />}
    </div>
  );
}

export default Blatur;
